import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Home, Loader2, Truck, Shield, FileText, AlertCircle,
  CheckCircle, Clock, XCircle, ExternalLink, Calendar, Hash,
  ClipboardList, ChevronRight,
} from 'lucide-react';
import { format } from 'date-fns';

const statusBadge = (status) => {
  const map = {
    available: { cls: 'bg-green-500/20 text-green-400 hover:bg-green-500/30', label: 'Available' },
    in_use: { cls: 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30', label: 'In Use' },
    out_of_service: { cls: 'bg-red-500/20 text-red-400 hover:bg-red-500/30', label: 'Out of Service' },
    maintenance: { cls: 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30', label: 'Maintenance' },
  };
  return map[status] || { cls: 'bg-gray-500/20 text-[var(--m-text-secondary)] hover:bg-gray-500/30', label: status || 'Unknown' };
};

const expiryBadge = (status) => {
  const map = {
    valid: { cls: 'bg-green-500/20 text-green-400 hover:bg-green-500/30', icon: CheckCircle, label: 'Valid' },
    expiring_soon: { cls: 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30', icon: Clock, label: 'Expiring Soon' },
    expired: { cls: 'bg-red-500/20 text-red-400 hover:bg-red-500/30', icon: XCircle, label: 'Expired' },
    no_expiry: { cls: 'bg-gray-500/20 text-[var(--m-text-secondary)] hover:bg-gray-500/30', icon: Shield, label: 'No Expiry' },
  };
  return map[status] || map.no_expiry;
};

const MobileEquipmentDetail = () => {
  const navigate = useNavigate();
  const { equipmentId } = useParams();
  const { api, hasFeature } = useMobileAuth();
  const [equipment, setEquipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assetForms, setAssetForms] = useState([]);
  const [assetSubmissions, setAssetSubmissions] = useState([]);

  useEffect(() => {
    const fetchEquipment = async () => {
      try {
        const res = await api.get(`/api/mobile/equipment/${equipmentId}`);
        setEquipment(res.data);
      } catch (err) {
        console.error('Failed to fetch equipment detail:', err);
        if (err?.response?.status === 403) {
          toast.error('You do not have access to this asset');
        } else {
          toast.error('Failed to load asset');
        }
      } finally {
        setLoading(false);
      }
    };
    if (equipmentId) fetchEquipment();
  }, [api, equipmentId]);

  // Fetch asset-linked form templates and submissions for this asset
  useEffect(() => {
    if (!equipmentId || !hasFeature('forms')) return;
    const fetchForms = async () => {
      try {
        const [templatesRes, subsRes] = await Promise.all([
          api.get('/api/form-templates?form_type=asset_linked'),
          api.get(`/api/form-submissions?asset_id=${equipmentId}&mine=true`),
        ]);
        setAssetForms(templatesRes.data.filter(t => t.is_active));
        setAssetSubmissions(subsRes.data || []);
      } catch {
        // Non-critical — silently fail
      }
    };
    fetchForms();
  }, [api, equipmentId, hasFeature]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--m-bg)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  if (!equipment) {
    return (
      <MobileLayout>
        <div className="min-h-full bg-[var(--m-bg)] flex flex-col items-center justify-center p-4">
          <AlertCircle className="h-12 w-12 text-[var(--m-text-muted)] mb-3" />
          <p className="text-[var(--m-text-secondary)]">Asset not found</p>
          <button onClick={() => navigate(-1)} className="mt-4 text-[#ffd108] text-sm">Go back</button>
        </div>
      </MobileLayout>
    );
  }

  const compliance = equipment.compliance_records || [];
  const files = equipment.files || [];
  const s = statusBadge(equipment.status);

  return (
    <MobileLayout>
      <div className="min-h-full bg-[var(--m-bg)]" data-testid="mobile-equipment-detail">
        {/* Header */}
        <div className="bg-[var(--m-bg-card)] px-4 py-3 flex items-center justify-between sticky top-0 z-10 safe-top">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-[var(--m-text-secondary)] p-1" data-testid="back-btn">
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <Truck className="h-5 w-5 text-[#ffd108] flex-shrink-0" />
              <h1 className="text-[var(--m-text)] font-semibold truncate">{equipment.asset_id || equipment.name || 'Equipment'}</h1>
            </div>
          </div>
          <button onClick={() => navigate('/mobile/dashboard')} className="text-[var(--m-text-secondary)] p-1" data-testid="home-btn">
            <Home className="h-5 w-5" />
          </button>
        </div>

        {/* Photo */}
        {equipment.photo_url && (
          <div className="px-4 pt-3">
            <img src={equipment.photo_url} alt={equipment.asset_id} className="w-full max-h-48 rounded-lg object-cover bg-black/30" />
          </div>
        )}

        {/* Details card */}
        <div className="px-4 py-3">
          <div className="bg-[var(--m-bg-card)] rounded-lg p-4 space-y-3" data-testid="equipment-details-card">
            <div className="flex items-center justify-between">
              <h2 className="text-[var(--m-text)] font-semibold">{equipment.asset_id || equipment.name}</h2>
              <Badge className={s.cls}>{s.label}</Badge>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              {equipment.equipment_type && (
                <div>
                  <p className="text-xs text-[var(--m-text-muted)]">Type</p>
                  <p className="text-[var(--m-text-body)]">{equipment.equipment_type}</p>
                </div>
              )}
              {equipment.capacity && (
                <div>
                  <p className="text-xs text-[var(--m-text-muted)]">Capacity</p>
                  <p className="text-[var(--m-text-body)]">{equipment.capacity}</p>
                </div>
              )}
              {equipment.registration && (
                <div>
                  <p className="text-xs text-[var(--m-text-muted)]">Registration</p>
                  <p className="text-[var(--m-text-body)]">{equipment.registration}</p>
                </div>
              )}
              {equipment.serial_number && (
                <div>
                  <p className="text-xs text-[var(--m-text-muted)]">Serial Number</p>
                  <p className="text-[var(--m-text-body)]">{equipment.serial_number}</p>
                </div>
              )}
              {equipment.year && (
                <div>
                  <p className="text-xs text-[var(--m-text-muted)]">Year</p>
                  <p className="text-[var(--m-text-body)]">{equipment.year}</p>
                </div>
              )}
              {equipment.make && (
                <div>
                  <p className="text-xs text-[var(--m-text-muted)]">Make</p>
                  <p className="text-[var(--m-text-body)]">{equipment.make}</p>
                </div>
              )}
              {equipment.model && (
                <div>
                  <p className="text-xs text-[var(--m-text-muted)]">Model</p>
                  <p className="text-[var(--m-text-body)]">{equipment.model}</p>
                </div>
              )}
              {equipment.supplier_name && (
                <div>
                  <p className="text-xs text-[var(--m-text-muted)]">Supplier</p>
                  <p className="text-[var(--m-text-body)]">{equipment.supplier_name}</p>
                </div>
              )}
            </div>

            {equipment.notes && (
              <div>
                <p className="text-xs text-[var(--m-text-muted)]">Notes</p>
                <p className="text-[var(--m-text-body)] text-sm whitespace-pre-wrap">{equipment.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Compliance Records */}
        <div className="px-4 pb-3">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-4 w-4 text-[#ffd108]" />
            <h3 className="text-[var(--m-text)] font-medium text-sm">Compliance Records</h3>
            <span className="text-xs text-[var(--m-text-muted)]">({compliance.length})</span>
          </div>

          {compliance.length === 0 ? (
            <div className="bg-[var(--m-bg-card)] rounded-lg p-4 text-center">
              <p className="text-[var(--m-text-muted)] text-sm">No compliance records</p>
            </div>
          ) : (
            <div className="space-y-2">
              {compliance.map((rec) => {
                const eb = expiryBadge(rec.expiry_status);
                const Icon = eb.icon;
                return (
                  <div key={rec.id} className="bg-[var(--m-bg-card)] rounded-lg p-3" data-testid={`compliance-record-${rec.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-[var(--m-text)] text-sm font-medium">{rec.compliance_type_name}</p>
                        {rec.sub_type_names?.length > 0 && (
                          <p className="text-xs text-[var(--m-text-secondary)] mt-0.5">{rec.sub_type_names.join(', ')}</p>
                        )}
                      </div>
                      <Badge className={`text-[10px] flex-shrink-0 ${eb.cls}`}>
                        <Icon className="h-3 w-3 mr-0.5" />{eb.label}
                      </Badge>
                    </div>

                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-[var(--m-text-secondary)]">
                      {rec.expiry_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Expires: {(() => { try { return format(new Date(rec.expiry_date), 'dd/MM/yyyy'); } catch { return rec.expiry_date; } })()}
                        </span>
                      )}
                      {rec.days_until_expiry != null && rec.expiry_status !== 'no_expiry' && (
                        <span className={rec.expiry_status === 'expired' ? 'text-red-400' : rec.expiry_status === 'expiring_soon' ? 'text-yellow-400' : ''}>
                          {rec.expiry_status === 'expired' ? `${rec.days_until_expiry}d overdue` : `${rec.days_until_expiry}d remaining`}
                        </span>
                      )}
                      {rec.reference_number && (
                        <span className="flex items-center gap-1">
                          <Hash className="h-3 w-3" />
                          {rec.reference_number}
                        </span>
                      )}
                    </div>

                    {rec.document_url && (
                      <a
                        href={rec.document_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#ffd108] hover:underline"
                        data-testid={`compliance-doc-${rec.id}`}
                      >
                        <FileText className="h-3 w-3" />
                        View Document
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Asset-Linked Forms */}
        {hasFeature('forms') && assetForms.length > 0 && (
          <div className="px-4 pb-3" data-testid="asset-forms-section">
            <div className="flex items-center gap-2 mb-2">
              <ClipboardList className="h-4 w-4 text-[#ffd108]" />
              <h3 className="text-[var(--m-text)] font-medium text-sm">Asset Forms</h3>
              <span className="text-xs text-[var(--m-text-muted)]">({assetForms.length})</span>
            </div>
            <div className="space-y-2">
              {assetForms.map((form) => {
                const subs = assetSubmissions.filter(s => s.template_id === form.id);
                return (
                  <button
                    key={form.id}
                    onClick={() => navigate(`/mobile/forms/submit?templateId=${form.id}&assetId=${equipmentId}`)}
                    className="w-full p-3 bg-[var(--m-bg-card)] rounded-lg text-left hover:bg-[var(--m-bg-elevated)] transition-colors"
                    data-testid={`asset-form-${form.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-[var(--m-text)] text-sm font-medium">{form.name}</p>
                        {form.description && (
                          <p className="text-xs text-[var(--m-text-secondary)] mt-0.5 truncate">{form.description}</p>
                        )}
                        {subs.length > 0 && (
                          <div className="flex items-center gap-1 mt-1">
                            <CheckCircle className="h-3 w-3 text-green-400" />
                            <span className="text-xs text-green-400">{subs.length} submitted</span>
                          </div>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-[var(--m-text-secondary)] flex-shrink-0" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Attached Files */}
        {files.length > 0 && (
          <div className="px-4 pb-8">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-4 w-4 text-[#ffd108]" />
              <h3 className="text-[var(--m-text)] font-medium text-sm">Files</h3>
              <span className="text-xs text-[var(--m-text-muted)]">({files.length})</span>
            </div>
            <div className="space-y-2">
              {files.map((file) => (
                <a
                  key={file.id}
                  href={file.url || file.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-[var(--m-bg-card)] rounded-lg p-3 hover:bg-[var(--m-bg-elevated)] transition-colors"
                  data-testid={`file-${file.id}`}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-[var(--m-text-secondary)] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[var(--m-text)] text-sm truncate">{file.filename || file.name || 'File'}</p>
                      <p className="text-xs text-[var(--m-text-muted)]">{file.entity_type || 'attachment'}</p>
                    </div>
                    <ExternalLink className="h-4 w-4 text-[var(--m-text-muted)] flex-shrink-0" />
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Bottom spacing if no files */}
        {files.length === 0 && <div className="pb-8" />}
      </div>
    </MobileLayout>
  );
};

export default MobileEquipmentDetail;
